import React, { useState, useEffect, useContext, useCallback } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, startOfWeek, endOfWeek, isSameDay, getDay } from 'date-fns';
import { de } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon, CalendarIcon, PlusIcon, ClockIcon, UserIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import { hasAdminRights } from '../utils/roleUtils';
import EventModal from './EventModal';
import { mapApiEvent } from '../utils/calendarEvents';
import EditEventModal from './EditEventModal';
import MyDogsStatus from './widgets/MyDogsStatus';
import AddCalendarEvent from './AddCalendarEvent';
import ActionRequiredWidget from './ActionRequiredWidget';

const withAlpha = (color: string, alpha: string) => (
  /^#[0-9a-f]{6}$/i.test(color) ? `${color}${alpha}` : 'rgba(101, 163, 13, 0.1)'
);

const Calendar: React.FC = () => {
  const { user } = useContext(AuthContext);
  const isBoard = hasAdminRights(user);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [events, setEvents] = useState<any>({});
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [selectedDateEvents, setSelectedDateEvents] = useState<any[]>([]);
  const [highlightedCreatedDays, setHighlightedCreatedDays] = useState(new Set<string>());
  const [highlightedBookedDays, setHighlightedBookedDays] = useState(new Set<string>());
  // Schlüssel (Event-ID + Datum) aller Termine, die dem Nutzer "gehören"
  // (selbst gebucht ODER selbst erstellt) – Basis für "Mein nächster Termin".
  const [myEventKeys, setMyEventKeys] = useState(new Set<string>());
  const [editingEvent, setEditingEvent] = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  // Ob es offene Aufgaben gibt – steuert, ob der Header oben 60/40 mit dem
  // "Noch zu erledigen"-Widget geteilt wird oder volle Breite nimmt.
  const [hasTodos, setHasTodos] = useState(false);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  const monthEventCount = days
    .filter(day => isSameMonth(day, currentDate))
    .reduce((sum, day) => sum + (events[format(day, 'yyyy-MM-dd')]?.length || 0), 0);

  // Funktion zum Neuladen der Events
  const refreshCalendar = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/calendar');
      const data = response.data;
      if (data.success) {
        // Mapping liegt in utils/calendarEvents, damit andere Seiten (Dashboard)
        // dasselbe EventModal mit identischer Datenform öffnen können.
        const formattedEvents = data.events.reduce((acc: any, event: any) => {
          const mapped = mapApiEvent(event);
          const dateStr = format(new Date(event.start), 'yyyy-MM-dd');
          if (!acc[dateStr]) acc[dateStr] = [];
          acc[dateStr].push(mapped);
          return acc;
        }, {});
        setEvents(formattedEvents);
      }
    } catch (err: any) {
      if (err.response?.status !== 401) {
        console.error('Fehler beim Abrufen der Kalendereinträge:', err);
      }
    }
  }, []);

  const fetchUserEvents = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/calendar/user-events-calendar/unknown');
      const data = response.data;
      if (data.eventsCreated) {
        const createdDates = new Set<string>(data.eventsCreated.map((event: any) => event.start));
        setHighlightedCreatedDays(createdDates);
      }
      if (data.eventsBooked) {
        const bookedDates = new Set<string>(data.eventsBooked.map((event: any) => event.start));
        setHighlightedBookedDays(bookedDates);
      }
      // "Meine" Termine: NUR echte Buchungen (nicht selbst angelegte Termine),
      // als Schlüssel id_YYYY-MM-DD (Datum eingrenzen, damit an vollen Tagen/
      // Serien die richtige Occurrence trifft). Ohne Buchung bleibt der Banner leer.
      const toKey = (event: any) => `${event.id}_${String(event.start).slice(0, 10)}`;
      setMyEventKeys(new Set<string>((data.eventsBooked || []).map(toKey)));
    } catch (err: any) {
      if (err.response?.status !== 401) {
        console.error('Fehler beim Abrufen der Benutzer-Events:', err);
      }
    }
  }, []);

  useEffect(() => {
    refreshCalendar();
    fetchUserEvents();
  }, [refreshCalendar, fetchUserEvents]);

  const getEventForDay = (day: Date) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return events[dateStr] || [];
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const handleDateClick = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    setSelectedDate(date);
    const eventsOnDate = events[dateStr] || [];
    setSelectedDateEvents(eventsOnDate);
    
    // On mobile: if only one event, select it directly
    const isMobile = window.innerWidth < 640;
    if (isMobile && eventsOnDate.length === 1) {
      setSelectedEvent(eventsOnDate[0]);
    } else {
      setSelectedEvent(null);
    }
  };

  const handleEventClick = (event: any) => {
    // Set both states together to avoid inconsistencies
    setSelectedEvent(event);
    setSelectedDateEvents((prevEvents) => {
      // Ensure selectedDateEvents contains at least this event
      if (!prevEvents.some(e => e.id === event.id)) {
        return [event];
      }
      return prevEvents;
    });
  };

  const closeModal = () => {
    // Reset all modal-related states together
    setSelectedEvent(null);
    setSelectedDateEvents([]);
    setSelectedDate(null);
  };

  const handleDeleteEvent = (deleteResponse: any) => {
    if (deleteResponse && deleteResponse.success && deleteResponse.event_ids) {
      const eventIdsToDelete = deleteResponse.event_ids.map(String);
      setEvents((prevEvents: any) => {
        const newEvents = { ...prevEvents };
        Object.keys(newEvents).forEach((date) => {
          newEvents[date] = newEvents[date].filter(
            (event: any) => !eventIdsToDelete.includes(String(event.id))
          );
          if (newEvents[date].length === 0) delete newEvents[date];
        });
        return newEvents;
      });
      setSelectedEvent(null);
      setSelectedDateEvents([]);
    }
  };

  const handleBookEvent = (id: number, bookingCount: number) => {
    setEvents((prevEvents: any) => {
      const newEvents = { ...prevEvents };
      Object.keys(newEvents).forEach((date) => {
        newEvents[date] = newEvents[date].map((event: any) =>
          event.id === id
            ? { ...event, extendedProps: { ...event.extendedProps, bookingCount } }
            : event
        );
      });
      return newEvents;
    });
  };

  const handleAddEvent = (newEvent: any) => {
    const dateStr = format(newEvent.start, 'yyyy-MM-dd');
    setEvents((prevEvents: any) => ({
      ...prevEvents,
      [dateStr]: [
        ...(prevEvents[dateStr] || []),
        {
          id: newEvent.id,
          title: newEvent.title,
          start: newEvent.start,
          end: newEvent.end,
          extendedProps: {
            color: newEvent.color,
            bookable: newEvent.bookable,
            bookingCount: newEvent.bookingCount,
            creator: {
              name: newEvent.creator,
              id: newEvent.creator_id,
            },
            cotrainer: newEvent.cotrainer || null,
            repeat: newEvent.repeat,
            repeat_end: newEvent.repeat_end,
            unique_id: newEvent.unique_id,
            taetigkeitsbericht: newEvent.taetigkeitsbericht || 0,
            taetigkeitsbericht_text: newEvent.taetigkeitsbericht_text || '',
          },
        },
      ],
    }));
    setHighlightedCreatedDays(prev => new Set([...Array.from(prev), dateStr]));
  };

  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [addEventDate, setAddEventDate] = useState<Date | null>(null);

  // Nur die eigenen Termine (gebucht oder selbst erstellt), nicht der nächste
  // beliebige Vereinstermin – passend zum "Meine Buchungen"-Kontext.
  const nextEvent = Object.values(events)
    .flatMap((dayEvents: any) => dayEvents)
    .filter((event: any) =>
      event?.start instanceof Date &&
      (event.end || event.start).getTime() >= Date.now() &&
      myEventKeys.has(`${event.id}_${format(event.start, 'yyyy-MM-dd')}`))
    .sort((eventA: any, eventB: any) => eventA.start.getTime() - eventB.start.getTime())[0] as any | undefined;

  const openNextEvent = () => {
    if (!nextEvent) return;

    const eventDate = new Date(nextEvent.start);
    const dateKey = format(eventDate, 'yyyy-MM-dd');
    setSelectedDate(eventDate);
    setSelectedDateEvents(events[dateKey] || [nextEvent]);
    setSelectedEvent(nextEvent);
  };

  const nextEventTime = nextEvent
    ? `${format(nextEvent.start, 'HH:mm')}–${format(nextEvent.end, 'HH:mm')} Uhr`
    : '';

  const nextEventDate = nextEvent
    ? isToday(nextEvent.start)
      ? `Heute · ${nextEventTime}`
      : `${format(nextEvent.start, 'EEEE, d. MMMM', { locale: de })} · ${nextEventTime}`
    : '';

  const nextEventTrainer = nextEvent?.extendedProps?.creator?.name;

  return (
    <div className="mx-auto w-full max-w-7xl space-y-5 pb-8">
      <div className={`grid gap-3 sm:gap-4 ${hasTodos ? 'lg:grid-cols-5' : ''}`}>
      <header className={`app-page-header flex items-center overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-[#1c2432] ${hasTodos ? 'lg:col-span-3' : ''}`}>
        <div className="flex w-full flex-col gap-5 p-5 sm:p-7 md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
              <CalendarIcon className="h-6 w-6" />
            </div>
            {nextEvent ? (
              <button
                type="button"
                onClick={openNextEvent}
                className="group min-w-0 text-left"
                aria-label={`${nextEvent.title} – Details anzeigen`}
              >
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary-600 dark:text-primary-400">Mein nächster Termin</p>
                <h1 className="mt-1 truncate text-2xl font-bold text-gray-900 transition group-hover:text-primary-700 dark:text-white dark:group-hover:text-primary-400 sm:text-[28px]">
                  {nextEvent.title}
                </h1>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
                  <span className="inline-flex items-center gap-1.5 capitalize">
                    <ClockIcon className="h-4 w-4 shrink-0" />
                    {nextEventDate}
                  </span>
                  {nextEventTrainer && nextEventTrainer !== 'Unbekannt' && (
                    <span className="inline-flex items-center gap-1.5">
                      <UserIcon className="h-4 w-4 shrink-0" />
                      {nextEventTrainer}
                    </span>
                  )}
                </div>
              </button>
            ) : (
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary-600 dark:text-primary-400">Mein nächster Termin</p>
                <h1 className="mt-1 text-2xl font-bold text-gray-900 dark:text-white sm:text-[28px]">Keine anstehenden Buchungen</h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Sobald du einen Termin buchst oder anlegst, erscheint er hier.</p>
              </div>
            )}
          </div>
          <Link to="/my-bookings" className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl bg-gray-100 px-4 text-sm font-bold text-gray-700 transition hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600">
            Meine Buchungen <ArrowRightIcon className="h-4 w-4" />
          </Link>
        </div>
      </header>

      <ActionRequiredWidget
        onCountChange={(n) => setHasTodos(n > 0)}
        className={hasTodos ? 'lg:col-span-2' : ''}
      />
      </div>

      <div className="grid items-start gap-3 sm:gap-4">
        <main className="order-2 min-w-0">
      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-[#1c2432]">
        {/* Header */}
        <div className="border-b border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-[#1c2432] sm:p-5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 dark:bg-primary-900/20">
                <CalendarIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">Vereinskalender</p>
                <h2 className="truncate text-base font-bold capitalize text-gray-900 dark:text-white sm:text-xl">{format(currentDate, 'MMMM yyyy', { locale: de })}</h2>
              </div>
              <span className="hidden rounded-full bg-gray-100 px-2.5 py-1 text-xs font-bold text-gray-500 dark:bg-gray-700 dark:text-gray-300 sm:inline-flex">{monthEventCount} Termine</span>
            </div>
            <div className="flex items-center gap-2">
              {/* Plus button for board members */}
              {isBoard && (
                <button
                  onClick={() => {
                    setAddEventDate(selectedDate);
                    setShowAddEventModal(true);
                  }}
                  className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-600 text-white transition hover:bg-primary-700"
                  title="Event hinzufügen"
                  aria-label="Event hinzufügen"
                >
                  <PlusIcon className="h-5 w-5" />
                </button>
              )}
              <button
                onClick={goToToday}
                className="hidden h-10 items-center rounded-xl bg-gray-100 px-3 text-sm font-bold text-gray-700 transition hover:bg-gray-200 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 sm:flex"
              >
                Heute
              </button>
              <div className="flex h-10 items-center overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-700">
                <button
                  onClick={previousMonth}
                  className="flex h-10 w-10 items-center justify-center text-gray-700 transition hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-600"
                  aria-label="Vorheriger Monat"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={nextMonth}
                  className="flex h-10 w-10 items-center justify-center text-gray-700 transition hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-600"
                  aria-label="Nächster Monat"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Calendar Grid */}
        <div className="p-2 sm:p-4">
          {/* Weekday Headers */}
          <div className="mb-1 grid grid-cols-7 sm:mb-2">
            {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((day, index) => (
              <div 
                key={day} 
                className={`py-2 text-center text-[10px] font-bold uppercase tracking-wider sm:text-xs ${
                  index >= 5 ? 'text-primary-600 dark:text-primary-400' : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                <span className="truncate">{day}</span>
              </div>
            ))}
          </div>
          
          {/* Calendar Days Grid */}
          <div className="grid grid-cols-7 gap-px overflow-hidden rounded-xl border border-gray-200 bg-gray-200 dark:border-gray-700 dark:bg-gray-700">
            {days.map(day => {
              const dayEvents = getEventForDay(day);
              const dateStr = format(day, 'yyyy-MM-dd');
              const isCreated = highlightedCreatedDays.has(dateStr);
              const isBooked = highlightedBookedDays.has(dateStr) && !isCreated;
              const isWeekend = getDay(day) === 0 || getDay(day) === 6;
              const dayNumber = format(day, 'd');
              
              return (
                <div
                  key={day.toString()}
                  onClick={() => handleDateClick(day)}
                  className={`
                    relative cursor-pointer bg-white transition-colors dark:bg-[#1c2432]
                    ${!isSameMonth(day, currentDate) ? 'opacity-40' : ''}
                    ${selectedDate && isSameDay(day, selectedDate) ? 'z-10 ring-2 ring-inset ring-primary-500' : ''}
                    ${isToday(day) ? 'bg-primary-50 dark:bg-primary-900/10' : ''}
                    hover:bg-gray-50 dark:hover:bg-gray-800/70
                  `}
                >
                  {/* Mobile View */}
                  <div className="p-1 sm:hidden">
                    <div className="flex flex-col items-center">
                      <div className={`
                        relative flex h-10 w-10 items-center justify-center rounded-full
                        ${isToday(day) ? 'bg-primary-600 text-white' : ''}
                        ${isWeekend && !isToday(day) ? 'text-primary-500 dark:text-primary-400' : ''}
                        ${!isWeekend && !isToday(day) ? 'text-gray-900 dark:text-gray-100' : ''}
                        ${isCreated ? 'text-primary-600 dark:text-primary-400' : ''}
                        ${isBooked && !isCreated ? 'text-success-600 dark:text-success-400' : ''}
                      `}>
                        <span className="text-sm font-medium">{dayNumber}</span>
                      </div>
                      {dayEvents.length > 0 && (
                        <div className="flex gap-1 mt-1">
                          {dayEvents.slice(0, 3).map((event: any, idx: number) => (
                            <div
                              key={idx}
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: event.extendedProps?.color || '#3B82F6' }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Desktop View */}
                  <div className={`hidden min-h-[108px] p-2 sm:block md:min-h-[120px] xl:min-h-[132px] ${
                    isWeekend && isSameMonth(day, currentDate) ? 'bg-gray-50/70 dark:bg-gray-800/30' : ''
                  }`}>
                    <div className="flex justify-between items-start mb-1">
                      <span className={`
                        text-sm font-semibold inline-flex items-center justify-center transition-all duration-200
                        ${isToday(day) ? 'bg-primary-600 text-white w-7 h-7 rounded-full' : ''}
                        ${isWeekend && !isToday(day) ? 'text-primary-600 dark:text-primary-400' : ''}
                        ${!isWeekend && !isToday(day) ? 'text-gray-700 dark:text-gray-300' : ''}
                      `}>
                        {dayNumber}
                      </span>
                      {/* Indicator for booked/created */}
                      {(isBooked || isCreated) && (
                        <div className={`w-2 h-2 rounded-full ${isBooked ? 'bg-success-500' : 'bg-primary-500'}`} />
                      )}
                    </div>
                    
                    {/* Desktop Events */}
                    {dayEvents.length > 0 && (
                      <div className="space-y-1">
                        {dayEvents.slice(0, 3).map((event: any, idx: number) => {
                          const bookable = event.extendedProps?.bookable || 0;
                          const bookingCount = event.extendedProps?.bookingCount || 0;
                          const hasBookings = bookable > 0;
                          const isFull = bookingCount >= bookable;
                          const isAlmostFull = bookingCount > bookable * 0.8;

                          return (
                            <div
                              key={idx}
                              className="group relative"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEventClick(event);
                              }}
                            >
                              <div
                                className="cursor-pointer rounded-md border-l-[3px] px-1.5 py-1 text-[11px] font-semibold text-gray-700 transition hover:brightness-95 dark:text-gray-100"
                                style={{
                                  backgroundColor: withAlpha(event.extendedProps?.color || '#65a30d', '18'),
                                  borderLeftColor: event.extendedProps?.color || '#65a30d',
                                }}
                              >
                                <div className="flex items-center justify-between gap-1">
                                  <div className="flex items-center gap-1 min-w-0 flex-1">
                                    <ClockIcon className="h-3 w-3 flex-shrink-0 text-gray-400" />
                                    <span className="font-semibold">{format(event.start, 'HH:mm')}</span>
                                  </div>
                                  {hasBookings && (
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold flex-shrink-0 ${
                                      isFull
                                        ? 'bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-300'
                                        : isAlmostFull
                                        ? 'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300'
                                        : 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-300'
                                    }`}>
                                      {bookingCount}/{bookable}
                                    </span>
                                  )}
                                </div>
                                <div className="truncate mt-0.5">{event.title}</div>
                              </div>
                            </div>
                          );
                        })}
                        {dayEvents.length > 3 && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 pl-1.5">
                            +{dayEvents.length - 3} weitere
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 px-1 text-[10px] font-semibold text-gray-500 dark:text-gray-400 sm:text-xs">
            <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-primary-500" /> Von dir erstellt</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-success-500" /> Von dir gebucht</span>
            <button onClick={goToToday} className="ml-auto font-bold text-primary-600 hover:text-primary-700 dark:text-primary-400 sm:hidden">Heute anzeigen</button>
          </div>
        </div>
      </section>

      {/* Mobile Agenda View - Android Style - Show when date is selected and user is board member */}
      {selectedDate && (getEventForDay(selectedDate).length > 1 || (isBoard && getEventForDay(selectedDate).length === 0)) && (
        <div className="sm:hidden bg-white dark:bg-[#1c2432] rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
            {format(selectedDate, 'EEEE, d. MMMM', { locale: de })}
          </h3>
          <div className="space-y-3">
            {getEventForDay(selectedDate).map((event: any) => (
              <div
                key={event.id}
                onClick={() => handleEventClick(event)}
                className="flex items-start gap-3 p-3 rounded-xl border-l-4 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                style={{ borderLeftColor: event.extendedProps?.color || '#3B82F6' }}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <ClockIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {format(event.start, 'HH:mm')} - {format(event.end, 'HH:mm')}
                    </span>
                  </div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">{event.title}</h4>
                  {event.extendedProps?.creator && (
                    <div className="flex items-center gap-2 mt-2">
                      <UserIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {event.extendedProps.creator.name}
                      </span>
                    </div>
                  )}
                  {event.extendedProps?.bookable > 0 && (
                    <div className="mt-2 text-xs text-gray-500">
                      {event.extendedProps.bookingCount}/{event.extendedProps.bookable} Plätze belegt
                    </div>
                  )}
                </div>
                <ChevronRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-1" />
              </div>
            ))}
          </div>
        </div>
      )}
        </main>

        <aside className="order-1 grid min-w-0 items-start gap-3">
          <MyDogsStatus />
        </aside>
      </div>

      {/* Event Modal */}
      {(selectedEvent || selectedDateEvents.length > 0) && (
        <EventModal
          event={selectedEvent}
          events={selectedDateEvents}
          onClose={closeModal}
          onDelete={handleDeleteEvent}
          onBook={handleBookEvent}
          refreshCalendar={refreshCalendar}
          onEventSelect={setSelectedEvent}
          onEdit={(event: any) => {
            setEditingEvent(event);
            setShowEditModal(true);
            closeModal();
          }}
        />
      )}
      
      {/* Edit Event Modal */}
      {showEditModal && editingEvent && (
        <EditEventModal
          event={editingEvent}
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingEvent(null);
          }}
          refreshCalendar={refreshCalendar}
        />
      )}
      
      {/* Add Event Modal */}
      {isBoard && (
        <AddCalendarEvent
          isOpen={showAddEventModal}
          onClose={() => {
            setShowAddEventModal(false);
            setAddEventDate(null);
          }}
          selectedDate={addEventDate}
          onAddEvent={(event) => {
            handleAddEvent(event);
            setShowAddEventModal(false);
            setAddEventDate(null);
          }}
          refreshCalendar={() => {
            refreshCalendar();
            setShowAddEventModal(false);
            setAddEventDate(null);
          }}
        />
      )}
    </div>
  );
};

export default Calendar;
